import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import Footer from '../Footer';
import { 
    LayoutDashboard, 
    Music, 
    DollarSign, 
    Building2, 
    FileText, 
    LogOut,
    Menu,
    X,
    ChevronRight,
    BarChart3,
    Sun,
    Moon,
    Zap,
    Users,
    UserCog,
    Shield,
    FolderArchive,
    Wallet
} from 'lucide-react';

const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'founder'] },
    { path: '/admin/founder', label: 'Analytics', icon: Zap, roles: ['founder', 'executive'] },
    { path: '/admin/finance', label: 'Finance Dashboard', icon: Wallet, roles: ['finance'] },
    { path: '/admin/content', label: 'Content', icon: Music, roles: ['admin'] },
    { path: '/admin/revenue', label: 'Revenue', icon: DollarSign, roles: ['admin'] },
    { path: '/admin/labels', label: 'Labels', icon: Building2, roles: ['admin'] },
    { path: '/admin/invoices', label: 'Invoices', icon: FileText, roles: ['admin', 'founder', 'finance', 'executive'] },
    { path: '/admin/users', label: 'Users', icon: Users, roles: ['admin'] },
    { path: '/admin/assets', label: 'Assets', icon: FolderArchive, roles: ['admin', 'founder', 'executive'] },
    { path: '/admin/audit-logs', label: 'Audit Logs', icon: Shield, roles: ['admin', 'founder', 'executive'] },
    { path: '/admin/reports', label: 'Reports', icon: BarChart3, roles: ['admin', 'founder', 'finance', 'executive'] },
];

const AdminLayout = () => {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    return (
        <div className="min-h-screen bg-background flex">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed lg:static inset-y-0 left-0 z-50
                w-64 bg-card border-r border-border/40
                transform transition-transform duration-300 ease-in-out
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="flex flex-col h-full">
                    {/* Client branding (EYP) — theme-aware */}
                    <div className="px-6 pt-4 pb-2">
                        <img
                            src={theme === 'dark' ? '/branding/eyp-white.png' : '/branding/eyp-black.png'}
                            alt="EYP Creations"
                            className="h-7 w-auto opacity-95"
                            data-testid="client-logo-sidebar"
                        />
                    </div>
                    {/* Logo */}
                    <div className="h-12 flex items-center justify-between px-6 border-b border-border/40">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground/70">Powered by</span>
                            <img 
                                src="/branding/elara-logo.png" 
                                alt="ELARA" 
                                className="h-5 w-auto opacity-80"
                            />
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="lg:hidden"
                            onClick={() => setSidebarOpen(false)}
                        >
                            <X className="h-5 w-5" />
                        </Button>
                    </div>

                    {/* Navigation */}
                    <ScrollArea className="flex-1 py-4">
                        <nav className="px-3 space-y-1">
                            {navItems
                                .filter(item => {
                                    // Filter based on roles array
                                    if (item.roles && !item.roles.includes(user?.role)) return false;
                                    return true;
                                })
                                .map((item) => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setSidebarOpen(false)}
                                    className={({ isActive }) => `
                                        flex items-center gap-3 px-3 py-2.5 rounded-lg
                                        transition-all duration-200 group
                                        ${isActive 
                                            ? 'bg-primary/10 text-primary border border-primary/20' 
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                                        }
                                        ${item.roles && item.roles.length === 1 && item.roles[0] === 'founder' ? 'border border-dashed border-primary/30' : ''}
                                    `}
                                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                    <item.icon className={`w-5 h-5 ${item.roles && item.roles.length === 1 && item.roles[0] === 'founder' ? 'text-primary' : ''}`} />
                                    <span className="font-medium">{item.label}</span>
                                    <ChevronRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                                </NavLink>
                            ))}
                        </nav>
                        
                        {/* Profile Link */}
                        <div className="px-3 mt-4 pt-4 border-t border-border/40">
                            <NavLink
                                to="/admin/profile"
                                onClick={() => setSidebarOpen(false)}
                                className={({ isActive }) => `
                                    flex items-center gap-3 px-3 py-2.5 rounded-lg
                                    transition-all duration-200 group
                                    ${isActive 
                                        ? 'bg-primary/10 text-primary border border-primary/20' 
                                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                                    }
                                `}
                            >
                                <UserCog className="w-5 h-5" />
                                <span className="font-medium">Profile</span>
                            </NavLink>
                        </div>
                        
                        {/* Theme Toggle */}
                        <div className="px-3 mt-2">
                            <Button
                                variant="ghost"
                                className="w-full justify-start text-muted-foreground"
                                onClick={toggleTheme}
                                data-testid="theme-toggle"
                            >
                                {theme === 'dark' ? (
                                    <>
                                        <Sun className="w-4 h-4 mr-2" />
                                        Light Mode
                                    </>
                                ) : (
                                    <>
                                        <Moon className="w-4 h-4 mr-2" />
                                        Dark Mode
                                    </>
                                )}
                            </Button>
                        </div>
                    </ScrollArea>

                    {/* User section */}
                    <div className="p-4 border-t border-border/40">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                <span className="text-sm font-medium text-primary">
                                    {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{user?.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            className="w-full justify-start text-muted-foreground hover:text-destructive"
                            onClick={handleLogout}
                            data-testid="logout-button"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>

<div className="mt-4 pt-4 border-t border-border/40 text-center">
    <p className="text-xs font-semibold tracking-[0.18em] text-foreground">
        ELARA
    </p>
    <p className="mt-1 text-[11px] text-muted-foreground">
        Music Business Intelligence
    </p>
    <p className="mt-1 text-[10px] text-muted-foreground/70">
        Version 1.0
    </p>
</div>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top bar */}
                <header className="h-16 border-b border-border/40 flex items-center px-6 gap-4 bg-card/50 backdrop-blur-sm sticky top-0 z-30">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="lg:hidden"
                        onClick={() => setSidebarOpen(true)}
                        data-testid="menu-toggle"
                    >
                        <Menu className="h-5 w-5" />
                    </Button>
                    <div className="flex-1" />
                    <div className="text-sm text-muted-foreground">
                        EYP Workspace
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 overflow-auto">
                    <div className="p-6 lg:p-8">
                        <Outlet />
                    </div>
                </main>
                
                {/* Footer */}
                <Footer />
            </div>
        </div>
    );
};

export default AdminLayout;
